"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { Topic } from "@/lib/blogTopics";

/**
 * Topic filter for /blog. URL-syncs via ?topic=... so the filter
 * state is shareable and Googlebot indexes one canonical view
 * per topic-filtered page.
 *
 * Receives the topic list as a prop — the server component on /blog
 * fetches it once and hands it down here, so the pill row renders
 * on the same request as the article grid.
 */
export default function BlogTopicPills({
  topics,
  active,
}: {
  topics: Topic[];
  active?: string;
}) {
  const router = useRouter();
  const params = useSearchParams();

  const goto = (topic?: string) => {
    const next = new URLSearchParams(params?.toString());
    if (topic) {
      next.set("topic", topic);
    } else {
      next.delete("topic");
    }
    const qs = next.toString();
    router.push(qs ? `/blog?${qs}` : "/blog");
  };

  const Pill = ({
    label,
    isActive,
    onClick,
  }: {
    label: string;
    isActive: boolean;
    onClick: () => void;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-4 py-1.5 text-sm font-medium transition ${
        isActive
          ? "border-[#EF2BA0] bg-[#EF2BA0] text-white"
          : "border-gray-200 bg-white text-[#120F30] hover:border-[#EF2BA0] hover:text-[#EF2BA0]"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="flex flex-wrap gap-2">
      <Pill label="All" isActive={!active} onClick={() => goto(undefined)} />
      {topics.map((t) => (
        <Pill
          key={t.name}
          label={t.label}
          isActive={active === t.name}
          onClick={() => goto(t.name)}
        />
      ))}
    </div>
  );
}
